import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { Botao, FormActions, PageHeader, Skeleton } from '../../../ui';
import DocumentoVistoria from '../../../components/vistoria/DocumentoVistoria';

import { RootState } from '../../../store/store';
import { buscarUsuarioPorId } from '../../../api/user/userApi';
import {
  selectChecklistItems,
  selectChecklistObservacao,
} from '../../../store/checklistAcessoriosSlice';
import { agruparChecklist } from '../../../utils/vistoriaAcessorios';
import { VISTORIA_ASSINATURA } from '../routes';

/**
 * `/vistoria/assinatura/previa` — a **prévia do laudo antes de assinar**
 * (reescrita na at-64, frente D/D2).
 *
 * 🔴 **Não é etapa do fluxo**, então não tem `Stepper`: é um desvio de leitura
 * aberto pela assinatura, de onde se volta para o mesmo lugar. (A rota antiga era
 * `/assinatura-termo/visualizar`, e o título da tela dizia "LAUDO DE VISTORIA" —
 * o endereço é que estava errado.)
 *
 * ## O que mudou
 *
 *   1. **O documento é o `DocumentoVistoria` compartilhado**, o mesmo do laudo e
 *      da visualização por veículo. Eram três cópias das mesmas seções.
 *   2. 🔴 **Três chamadas à API saíram.** A tela buscava associado
 *      (`fetchAssociadoPorId`), carro (`fetchCarroPorId`) e usuário — para exibir
 *      nome, CPF, placa e ano que **já estão no Redux**, preenchidos pelo painel
 *      ou pela carteira antes de o fluxo começar. Sobrou `buscarUsuarioPorId`, que
 *      é a única informação exclusiva desta tela: o **vistoriador**.
 *   3. **`Skeleton` no lugar do `FullScreenLoader`** — o loader cobria a tela
 *      inteira para buscar um nome.
 *   4. **A impressão não sai mais com o cabeçalho e o menu.** O `@media print` que
 *      escondia `.right-button` e `.logo-background` vivia no CSS desta tela (que
 *      saiu); a regra agora é do **shell** (`print:tw-hidden` no `AppShell` e no
 *      `PageHeader`), e vale para qualquer tela migrada que venha a imprimir.
 *
 * ⚠️ **"Imprimir" (`window.print()`) fica**: é o único caminho de impressão antes
 * de o PDF existir.
 *
 * 🔴 **O botão "Voltar para a assinatura" é a ação principal da tela** (pedido de
 * Victor, 10/09/2026). O "Voltar" do cabeçalho existe e funciona, mas é um ícone
 * no canto que não diz para onde vai — e aqui o consultor está com o associado ao
 * lado, lendo o laudo **antes de assinar**: voltar e assinar é o passo seguinte do
 * fluxo, não uma saída genérica. Ver o comentário junto ao botão sobre por que ele
 * navega para o destino nomeado em vez de usar o histórico.
 *
 * ⚠️ **A data/hora exibida é a de ABERTURA da tela**, como sempre foi. Não há
 * `geradoEm` na API, e inventar um valor seria pior; registrado no plano como
 * observação para Victor, não como correção.
 */
const VistoriaAssinaturaPreviaPage: React.FC = () => {
  const navigate = useNavigate();
  const voltar = useVoltar(VISTORIA_ASSINATURA());

  const cpfDoFluxo = useSelector((state: RootState) => state.vistoria.cpf);
  const veiculo = useSelector((state: RootState) => state.vistoria.vehicle);
  const associadoNome = useSelector((state: RootState) => state.associado.nomeCompleto);
  const associadoCpf = useSelector((state: RootState) => state.associado.cpfCnpj);

  const itensChecklist = useSelector(selectChecklistItems);
  const observacao = useSelector(selectChecklistObservacao);

  const userId = useSelector((state: RootState) => state.auth.userId);
  const emailDaSessao = useSelector((state: RootState) => state.auth.email);

  const [vistoriador, setVistoriador] = React.useState<{ nome?: string; email?: string } | null>(
    null,
  );
  const [carregando, setCarregando] = React.useState(Boolean(userId));

  /**
   * A hora em que a tela abriu — é o que a seção "Dados da Vistoria" mostra, e era
   * assim antes desta branch (`emittedAt = new Date()` no render).
   */
  const abertaEm = React.useMemo(() => new Date().toLocaleString('pt-BR'), []);

  React.useEffect(() => {
    if (!userId) return undefined;

    let ativo = true;
    setCarregando(true);

    buscarUsuarioPorId(userId)
      .then((dados: any) => {
        if (ativo) setVistoriador({ nome: dados?.nome ?? dados?.name, email: dados?.email });
      })
      .catch(() => {
        /*
         * ⚠️ Falha aqui NÃO é `ErrorState`: o documento inteiro não depende do
         * nome do vistoriador — o campo mostra "—" e a prévia segue servindo ao
         * seu propósito, que é o associado ler o laudo antes de assinar.
         */
        if (ativo) setVistoriador(null);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
    };
  }, [userId]);

  const checklist = React.useMemo(() => agruparChecklist(itensChecklist), [itensChecklist]);

  return (
    <ShellSaac
      cabecalho={<PageHeader sobretitulo="Vistoria" titulo="Prévia do laudo" aoVoltar={voltar} />}
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)] print:tw-border-0 print:tw-shadow-none">
            {carregando ? (
              <Skeleton linhas={5} altura="tw-h-14" />
            ) : (
              <DocumentoVistoria
                associado={{
                  nome: associadoNome,
                  cpf: associadoCpf || cpfDoFluxo,
                }}
                veiculo={{
                  placa: veiculo?.placa,
                  modelo: veiculo?.modelo,
                  anoModelo: veiculo?.anoModelo,
                  anoFabricacao: veiculo?.anoFabricacao,
                }}
                checklist={checklist}
                observacao={observacao}
                dataVistoria={abertaEm}
                vistoriador={{ nome: vistoriador?.nome, email: vistoriador?.email || emailDaSessao }}
                /*
                 * A prévia mostra o que EXISTE antes de assinar: dados, checklist
                 * e observação. Fotos, assinatura e PDF ficam de fora — a
                 * assinatura ainda não foi dada e o PDF só nasce no laudo.
                 */
                secoes={{ vistoria: true, fotos: false, assinatura: false, pdf: false }}
              />
            )}

            {/* ⚠️ `print:tw-hidden`: os botões não pertencem à folha impressa. */}
            <div className="print:tw-hidden">
              <FormActions>
                {/*
                  🔴 **O caminho de volta é a AÇÃO PRINCIPAL desta tela** (pedido de
                  Victor, 10/09/2026: *"não tem botão para voltar à tela Assinatura
                  do associado… tem que ter"*).

                  A tela nasceu com o "Voltar" do cabeçalho, e ele funciona — mas é
                  um ícone circular no canto (no mobile) que **não diz para onde
                  vai**. Aqui o consultor está com o associado ao lado, lendo o
                  laudo **antes de assinar**: a próxima coisa que ele vai fazer é
                  voltar e assinar, e isso é uma ação do fluxo, não uma saída
                  genérica.

                  ⚠️ Vai por `navigate` para o destino NOMEADO, e não pelo
                  histórico (`useVoltar`): um botão que promete "voltar para a
                  assinatura" tem de ir para a assinatura, mesmo se o histórico
                  disser outra coisa (recarregar a página, abrir por link direto).
                  O "Voltar" do cabeçalho continua sendo o do histórico.

                  📌 Navegar direto é seguro porque a tela de assinatura lê tudo do
                  Redux (`vistoriaId`, nome do associado) — o `location.state` que
                  os outros chamadores mandam não é lido por ela.
                */}
                {/* `larguraTotal` + `sm:w-auto`: coluna no mobile, lado a lado no
                    desktop — o padrão do projeto para mais de um botão na barra
                    (precedente em `/cotacao-produtos`, at-62). */}
                <Botao
                  variante="primario"
                  larguraTotal
                  className="sm:tw-w-auto"
                  onClick={() => navigate(VISTORIA_ASSINATURA())}
                >
                  Voltar para a assinatura
                </Botao>

                <Botao
                  variante="secundario"
                  larguraTotal
                  className="sm:tw-w-auto"
                  onClick={() => window.print()}
                >
                  Imprimir
                </Botao>
              </FormActions>
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default VistoriaAssinaturaPreviaPage;
